import { useToastStore } from "@/services/store";
import { Alert, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";
import { interval, timeInterval } from "rxjs";

const TOTAL_SECONDS_OPENED = 6; // Total time in seconds the toast will remain visible
const seconds = interval(25); // Observable emitting values every 25 milliseconds

/**
 * A functional component that displays a toast notification with a progress bar.
 *
 * The toast becomes visible when `isOpen` is true and will automatically close after
 * `TOTAL_SECONDS_OPENED` seconds, displaying a progress bar that fills up over time.
 *
 * @component
 * @returns {JSX.Element} The rendered toast notification.
 */
const Toast = () => {
  const toastState = useToastStore((state) => state.toastState);
  const handleClose = useToastStore((state) => state.handleClose);
  const { isOpen, message, type } = toastState;

  const [progress, setProgress] = useState<number>(0);

  // open Toast when isOpen is true, then start a timer and hide Toast when timer reach TOTAL_SECONDS_OPENED
  useEffect(() => {
    if (isOpen) {
      // Create an observable that emits every 25 milliseconds
      const observable = seconds.pipe(timeInterval()).subscribe((value) => {
        // Calculate the progress percentage
        const newProgress = ((value.value / 40) * 100) / TOTAL_SECONDS_OPENED;
        setProgress(newProgress);
        if (newProgress === 100) handleClose();
      });
      // Cleanup function to reset progress and unsubscribe from observable
      return () => {
        setProgress(0);
        observable.unsubscribe();
      };
    }
  }, [isOpen, message, type, handleClose]);

  return (
    <Snackbar open={isOpen} onClose={handleClose} id="alert-toast">
      <Alert
        severity={type}
        onClose={handleClose}
        sx={{
          width: "100%",
          borderBottom: "3px solid",
          borderImage: (theme) =>
            `linear-gradient(90deg, ${theme.palette[type].main} ${progress}%, ${theme.palette.tertiary.main} 0%, ${theme.palette.tertiary.main} 100%) 1`,
        }}>
        {message}
      </Alert>
    </Snackbar>
  );
};

export default Toast;
